<template>
  <div
    class="unbundled-results"
    id="searchByScheduleTabContent"
    data-test="searchByScheduleTabContent"
  >
    <b-tabs v-model="activeTab" v-if="!isOneWay">
      <b-tab-item
        v-for="(journeys, index) in visibleJourneys"
        :label="journeyLabel(index)"
        :key="index"
        :disabled="index > selectedJourneys.length"
      >
        <JourneyGroup
          @searchTargetedAirlines="
            (airlines) => $emit('searchTargetedAirlines', airlines)
          "
          :tripVal="tripVal"
          :segmentIndex="index"
          :journeys="journeys"
          :items="items"
          :itineraryPrices="itineraryPrices(items)"
        />
      </b-tab-item>
      <b-tab-item label="Summary" :disabled="selectedJourneys.length < 2">
        <div v-if="selectedItem.journeys.length === 0">
          No selected item to show.
        </div>
        <div v-else>
          <SearchByScheduleSummary :solution="selectedItem" />
        </div>
      </b-tab-item>
    </b-tabs>

    <!-- ONE-WAY -->
    <div v-else>
      <div v-for="(journeys, index) in visibleJourneys" :key="index">
        <JourneyGroup
          @searchTargetedAirlines="
            (airlines) => $emit('searchTargetedAirlines', airlines)
          "
          :tripVal="tripVal"
          :segmentIndex="index"
          :journeys="journeys"
          :items="items"
          :itineraryPrices="itineraryPrices(items)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment"
import JourneyGroup from "./JourneyGroup.vue"
import SearchByScheduleSummary from "./SearchByScheduleSummary.vue"
import { mapState } from "vuex"

export default {
  mixins: [],
  components: {
    JourneyGroup,
    SearchByScheduleSummary,
  },
  props: {
    items: {},
    formData: Object,
  },
  data() {
    return {
      activeTab: undefined,
      disabledTab: true,
    }
  },
  computed: {
    ...mapState(["bookTravel"]),
    ...mapState(["travellersAndTrips"]),
    travellerProfile() {
      return this.$store.getters[
        "travellersAndTrips/getCurrentTravellerProfile"
      ]
    },
    tripVal() {
      return this.formData.tripVal
    },

    selectedJourneys() {
      return this.bookTravel.selectedUnbundledJourneys
    },

    //return all selected journeys except the active tab (outbound or return)
    otherJourneys() {
      return this.bookTravel.selectedUnbundledJourneys.filter(
        (journey) => parseInt(journey.segmentIndex) - 1 != this.activeTab
      )
    },

    /*
    selectedItems() {
      let items = this.items
      let selectedJourneys = this.bookTravel.selectedUnbundledJourneys

      // Return all items if no journeys are selected
      if (selectedJourneys.length === 0) {
        return items
      }

      // Look for matching flight result that contains all of the selected journeys
      return items.filter((item) => {
        for (let i = 0; i < selectedJourneys.length; i++) {
          if (item.journeys[i].uniqueKey == selectedJourneys[i].uniqueKey) {
            return true
          }
        }
        return false
      })
    },
    */

    visibleItems() {
      let items = this.items
      const otherJourneys = this.otherJourneys

      // Return all items if no journeys are selected
      if (otherJourneys.length === 0) {
        return items
      }

      // Look for matching flight result that contains all of the selected journeys
      return items.filter((item) => {
        for (let i = 0; i < otherJourneys.length; i++) {
          const journeyForSegment = item.journeys.find(
            (journey) => journey.segmentIndex == otherJourneys[i].segmentIndex
          )

          if (journeyForSegment.uniqueKey == otherJourneys[i].uniqueKey) {
            return true
          }
        }
        return false
      })
    },

    //find proposed flight items that include all of the selected journeys
    matchingItems() {
      let items = this.items
      let currentJourneys = this.bookTravel.selectedUnbundledJourneys

      //look for matching flight result that contains all of the selected journeys
      let matchingItems = items.filter((item) => {
        return currentJourneys.every((journey) => {
          let itemJourneys = item.journeys.map((journey) => journey.uniqueKey)

          return itemJourneys.includes(journey.uniqueKey)
        })
      })

      return matchingItems
    },

    //if we have definitive flight item return it here, else return solution obj with empty array
    selectedItem() {
      let solution = { journeys: [] }

      if (this.matchingItems.length == 1) {
        solution = this.matchingItems[0]
      }

      return solution
    },

    visibleJourneys() {
      const items = this.visibleItems

      // Create a Map to store unique journeys for each segment
      const uniqueJourneysBySegment = new Map()

      // Iterate through each item
      for (const item of items) {
        if (item && Array.isArray(item.journeys)) {
          // Process each journey in the item
          for (const journey of item.journeys) {
            const segmentIndex = journey.segmentIndex
            const uniqueKey = journey.uniqueKey

            // If this segment hasn't been seen before, initialize it with a Map
            if (!uniqueJourneysBySegment.has(segmentIndex)) {
              uniqueJourneysBySegment.set(segmentIndex, new Map())
            }

            // Get the Map of unique journeys for this segment
            const segmentJourneys = uniqueJourneysBySegment.get(segmentIndex)

            // Only add this journey if we haven't seen this uniqueKey before
            if (!segmentJourneys.has(uniqueKey)) {
              segmentJourneys.set(uniqueKey, journey)
            }
          }
        }
      }

      // Convert the Map structure into an array of arrays
      // First, get all segment indices and sort them
      const segmentIndices = Array.from(uniqueJourneysBySegment.keys()).sort(
        (a, b) => a - b
      )

      // Create an array where each element is an array of journeys for a segment
      const result = segmentIndices.map((segmentIndex) => {
        const segmentJourneys = uniqueJourneysBySegment.get(segmentIndex)
        return Array.from(segmentJourneys.values())
      })

      return result
    },

    journeyCount() {
      return this.visibleJourneys.length
    },

    filters: {
      get() {
        return this.$store.state.flight.filters
      },
      set(value) {
        this.$store.commit("flight/SET_FILTERS", value)
      },
    },

    isOneWay() {
      return this.journeyCount === 1
    },
  },
  methods: {
    activateNextTab(tabIndex) {
      this.activeTab = parseInt(tabIndex)
    },
    //perform anchored search
    //TO DO: 1. Add cache key 2. Replace existing items with anchored search results 3. Switch back to original results when navigating back
    async loadMoreItems(journey) {
      if (journey.legs.length > 0) {
        let runNewApiSearch = true
        let lastApiSearchTime = this.$store.getters[
          "bookTravel/getAnchoredSearchTime"
        ](journey.uniqueKey)

        if (lastApiSearchTime !== null) {
          //Check time of last anchored search and see if it has expired

          //console.log (lastApiSearchTime);
          let searchExpirationTime =
            lastApiSearchTime + this.$appConfig.timeRefresh
          //console.log(searchExpirationTime);

          if (Date.now() > searchExpirationTime) {
            runNewApiSearch = true
            //console.log("previous anchored search expired");
          } else {
            runNewApiSearch = false
            //console.log("previous anchored search still ok");
          }
        }

        // make a new API call
        if (runNewApiSearch) {
          this.$store.commit("bookTravel/FLIGHT_SEARCH_LOADING_STATUS", true)
          this.$store.commit("bookTravel/FLIGHT_SEARCH_COMPLETE", false)

          // build a search object
          let anchoredSearch = {
            travelerId: this.travellerProfile.id,
            numberOfPassengers: 1,
            numberOfResults: 200,
            cacheResponse: true,
            cacheExpiration: 1200,
            itinerary: [
              {
                departureLocation: {
                  airport: this.formData.loc1.code,
                },
                arrivalLocation: {
                  airport: this.formData.loc2.code,
                },
                date: {
                  date: moment(this.formData.startDate.date).format(
                    "YYYY-MM-DD"
                  ),
                },
                anchoredSegments: [],
              },
              {
                departureLocation: {
                  airport: this.formData.loc2.code,
                },
                arrivalLocation: {
                  airport: this.formData.loc1.code,
                },
                date: {
                  date: moment(this.formData.endDate.date).format("YYYY-MM-DD"),
                },
              },
            ],
          }

          if (this.formData.startDate.advanced.tod !== "Anytime") {
            anchoredSearch.itinerary[0].date = this.setAdvancedDateTime(
              this.formData.startDate
            )
          }

          if (this.formData.endDate.advanced.tod !== "Anytime") {
            anchoredSearch.itinerary[1].date = this.setAdvancedDateTime(
              this.formData.endDate
            )
          }

          if (this.formData.cabinClass) {
            anchoredSearch.cabinClass = this.formData.cabinClass
          }

          let anchoredSegments = []

          for (let leg of journey.legs) {
            anchoredSegments.push({
              departureDate: {
                date: moment(leg.departingAt.date).format("YYYY-MM-DD"),
                time: leg.departingAt.time,
              },
              arrivalDate: {
                date: moment(leg.arrivingAt.date).format("YYYY-MM-DD"),
                time: leg.arrivingAt.time,
              },
              departureAirport: leg.departingAirport,
              arrivalAirport: leg.arrivingAirport,
              airlineCode:
                leg.operatingAirline.code !== null
                  ? leg.operatingAirline.code
                  : journey.airlineCode,
              flightNumber: leg.flightNumber,
            })
          }
          anchoredSearch.itinerary[0].anchoredSegments = anchoredSegments
          // console.log('anchoredSearch', anchoredSearch);

          if (
            this.$store.getters["travellersAndTrips/getTravellers"].tripType ===
            "business"
          ) {
            anchoredSearch.travelPolicyId = this.travellerProfile.travelPolicyId
            anchoredSearch.applyPolicy = true
          }

          let atcDetails = this.$store.state.bookTravel.atc
          if (atcDetails?.useAtcShopper) {
            anchoredSearch.useAtcShopper = true
            anchoredSearch.ticketNumbers = Array(atcDetails.ticketNumber)
            anchoredSearch.mandatoryAirlines = atcDetails.ticketRules.airlines
            anchoredSearch.applyPolicy = true

            const atcJourneys = atcDetails.journeys.map((journey) => {
              return {
                requestCode: journey.requestCode,
                stops: journey.stops,
              }
            })

            anchoredSearch.atcJourneys = atcJourneys
            anchoredSearch.excludeNdc = true //disable NDC results for ATC Shopper
            anchoredSearch.preferredAirlines = null //disable preferred airlines for ATC Shopper
          }

          this.$app
            .huddleLabsServerCall("amadeus/air/search", anchoredSearch)
            .then((result) => {
              if (result.data?.length > 0) {
                this.$emit("appendResults", {
                  results: result.data,
                  included: result.included,
                })

                //store the time of this search
                let timer = {
                  uniqueKey: journey.uniqueKey,
                  time: Date.now(),
                }
                this.$store.commit(
                  "bookTravel/SET_ANCHORED_SEARCH_TIMER",
                  timer
                )

                // Update URL if needed
                const cacheKey = result.meta.cacheKey
                if (cacheKey && this.$route.query.key !== cacheKey) {

                  this.$router.push({
                    path: "flights",
                    query: { key: cacheKey },
                  })

                  this.$store.commit("flight/SET_ANCHORED_SEARCH_CONTEXT", true)

                }
              }

              this.$store.commit(
                "bookTravel/FLIGHT_SEARCH_LOADING_STATUS",
                false
              )

              this.$store.commit("bookTravel/FLIGHT_SEARCH_COMPLETE", true)
            })
        }
      }
    },
    setAdvancedDateTime(obj) {
      console.dir("set adv date")

      let date = {
        date: moment(obj.date).format("YYYY-MM-DD"),
        time: null,
        timeWindow: 4,
        isDeparture: obj.advanced.isDeparture, //.toString(),
      }

      if (obj.advanced.tod == "Morning") {
        date.time = "8:00"
      }

      if (obj.advanced.tod == "Afternoon") {
        date.time = "13:00"
      }

      if (obj.advanced.tod == "Evening") {
        date.time = "18:00"
      }

      if (obj.advanced.tod == "Custom") {
        date.time = moment(obj.advanced.time).format("HH:mm")
        date.timeWindow = obj.advanced.hours
      }

      return date
    },
    itineraryPrices(items) {
      let priceMap = []
      // Loop through each recommendation
      for (let item of items) {
        let priceMapItem = {
          id: item.id,
          pricing: item.pricing,
          journeys: [],
        }
        // Loop through each recommendation's journeys
        for (let journey of item.journeys) {
          priceMapItem.journeys.push(journey.uniqueKey)
        }
        priceMap.push(priceMapItem)
      }

      return priceMap
    },
    updateFilters(key, value) {
      const newFilters = { ...this.filters, [key]: value }
      this.filters = newFilters
      this.onFilterChange(newFilters)
    },
    journeyLabel(index) {
      let label = " "
      // If one-way,
      if (this.journeyCount == 1) {
        return "One-Way" + label
      }
      // If roundtrip, show nice names for legs
      if (this.journeyCount == 2) {
        if (index == 0) {
          return "Outbound" + label
        } else if (index == 1) {
          return "Return" + label
        }
      }
      // Otherwise just show the leg number
      return "Leg #" + index
    },
  },
  mounted() {
    this.$store.commit(
      "bookTravel/SET_EXPECTED_UNBUNDLED_JOURNEYS_COUNT",
      this.visibleJourneys.length
    )
  },
  watch: {
    //Adds selected journey to backend filter
    activeTab(newVal) {
      if (newVal === 0 || newVal === 1) {
        this.updateFilters("selectedJourneys", this.otherJourneys)
      }
      if (newVal === 0) {
        //remove anchored search cache from URL and restore prior cache
        if (this.$store.state.flight.anchoredSearchContext) {
          this.$store.commit("flight/SET_ANCHORED_SEARCH_CONTEXT", false)
          this.$router.go(-1) 
        }
      }
    },
  },

  provide() {
    return {
      //handle the filter change event
      onJourneySelected: (journey) => {
        console.log("journey selected", journey)

        this.$store.commit("bookTravel/SELECT_UNBUNDLED_JOURNEY", journey)

        this.activateNextTab(journey.segmentIndex)
        // load more results for selected outbound flight
        if (journey.segmentIndex == 1) {
          this.loadMoreItems(journey)
        }
      },
      onJourneyUnselected: (journey) => {
        console.log("journey unselected", journey)
        const index = journey.segmentIndex - 1

        this.$store.commit("bookTravel/UNSELECT_UNBUNDLED_JOURNEY", index)

        this.activateNextTab(index)
      },
    }
  },
  inject: ["onFilterChange"],
}
</script>

<style scoped></style>
